import React, { useState, Fragment, useEffect } from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDataProvider, useTranslate } from 'react-admin';
import { usePopupState } from "material-ui-popup-state/hooks";
import colors from "../CustomizedUIs/Colors";
import Search from "../search";
import TableSchedulerHeaders from './table-scheduler-header';
import TableSchedulerItem from './table-scheduler-item';
import TablePagination from './table-pagination';
import Snackbar from '../common/snackbar';
import DialogCreateEditSchedule from './dialog-create-edit-schedule';
import { Add } from '@material-ui/icons'
import Loading from '../common/loading';

// Images
import icChevronRight from "../assets/icons/icChevronRight.png";
import icChevronLeft from "../assets/icons/icChevronLeft.png";
import icRefresh from "../assets/icons/icRefresh.png";
import { getTextPagination } from '../utils';

const pagination = { page: 1, perPage: 25 };
let TABLE_SORT = { field: 'name', order: 'ASC' };
const useStyles = makeStyles((theme) => ({
    selected: {
        backgroundColor: colors.lightBlue["900"]
    },
    button: {
        marginRight: 20,
        fontSize: 9
    },
    lastButton: {
        marginRight: 40,
        fontSize: 9
    },
    btnIconOnly: {
        width: 12,
        height: 12,
        padding: 6,
        border: 'solid 1.5px #e5f1f8',
        borderRadius: 7.5,
        cursor: 'pointer',

        '&:active': {
            opacity: 0.5
        }
    },
    dialogCloseBtn: {
        backgroundColor: theme.palette.common.white,
        marginLeft: 10
    },
    textCountPagination: {
        fontSize: 11,
        color: '#4f5064',
        fontFamily: 'UniviaPro-Medium',
        fontWeight: 500,
        letterSpacing: 'normal',
        lineHeight: '13.2px',
        fontStyle: 'normal',
        fontStretch: 'normal'
    }
}));

let timeoutDeleted = null;
function TabScheduler({ totalItems, setTotalItems, isCreate, isUpdate, isDelete }) {
    const classes = useStyles();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const popupState = usePopupState({ variant: 'popper', popupId: 'sendMessageDialog' });

    // STATES REGION
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [deleteSchedule, setDeleteSchedule] = useState('');
    const [order, setOrder] = useState({ field: 'name', order: 'ASC' });
    const [showSelectedHeader, setShowSelectedHeader] = useState([]);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [currentEditSchedule, setCurrentEditSchedule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [checkall, setCheckAll] = useState(false)
    // END STATES REGION

    useEffect(() => {
        getTemplates();
    }, []);

    const getTemplates = () => {
        setLoading(true);
        dataProvider('GET_LIST', 'schedule-template', {
            filter: {
                name: { contains: searchText.trim() }
            },
            pagination,
            sort: TABLE_SORT,
        }).then(({ data, total }) => {
            const newData = data.map(item => ({
                ...item,
                checked: false
            }));
            setSchedules(newData);
            setTotalItems(total);
            setLoading(false);

            calculateTotalPage(total, data.length);
        }).catch(error => {
            // TODO: Should show error message
            console.error('getTemplates error:', error)
            setLoading(false);
        });
    }

    const calculateTotalPage = (total, size) => {
        // CALCULATE TOTAL PAGES
        const totalPages = Math.ceil(total / pagination.perPage);
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
        }
        setPages(pages);
    }

    const onPrevious = () => {
        if (page > 1) {
            onChangePage(page - 1);
        }
    }

    const onNext = () => {
        if (page < pages.length) {
            onChangePage(page + 1);
        }
    }

    const onChangePage = (nextPage) => {
        console.log('ON Change page:', nextPage);
        if (nextPage !== page) {
            setPage(nextPage);
            pagination.page = nextPage;
            // TODO: Call api
            getTemplates();
        }
    }

    const onRefresh = () => {
        setSchedules([]);
        getTemplates();
    }

    const onUndoDeleted = () => {
        clearTimeout(timeoutDeleted);
        setDeleteSchedule('');
        getTemplates();
    }

    const onChangeSortable = (nextSortable) => {
        TABLE_SORT = nextSortable;
        setOrder(TABLE_SORT);
        getTemplates();
    }

    const onCheckAll = (e) => {
        const newSchedules = schedules.map(item => {
            if (checkall === false) {
                return {
                    ...item,
                    checked: true
                }
            }
            else {
                return {
                    ...item,
                    checked: false
                }
            }
        });
        const selectedItems = newSchedules.filter(item => item.checked);
        const isCheckAll = selectedItems.length === newSchedules.length;
        setCheckAll(isCheckAll);

        setSchedules(newSchedules);
        setShowSelectedHeader(selectedItems);
    }

    const onDeleteMany = () => {
        const ids = schedules.filter(item => item.checked).map(item => item.id);
        const newSchedules = schedules.filter(item => !item.checked);
        const msgText = ids.length === 1 ? '1 file deleted!' : ids.length + ' files deleted!';
        setShowSelectedHeader([]);
        setSchedules(newSchedules);
        setDeleteSchedule(msgText);

        timeoutDeleted = setTimeout(() => {
            setDeleteSchedule('');
            dataProvider('DELETE_MANY', 'schedule-template', {
                ids,
            }).then(res => {
                console.log('On Delete schedules:', res);
                if (schedules.length === ids.length) {
                    if (page === pages.length && page !== 1) {
                        pagination.page = page - 1;
                        setPage(pagination.page);
                    }
                }
                getTemplates();
            }).catch(error => {
                console.error('On Delete:', error);
            });
        }, 6000);
    }

    const onCheck = (id) => {
        const newSchedules = schedules.map(item => ({
            ...item,
            checked: item.id === id ? !item.checked : item.checked
        }));
        let isCheckAll = newSchedules.filter(item => item.checked).length === schedules.length;
        setCheckAll(isCheckAll);
        setSchedules(newSchedules)
        const newSelectedItems = newSchedules.filter(item => item.checked);
        setShowSelectedHeader(newSelectedItems);
    }

    const onDeleteOne = (id) => {
        const newSchedules = schedules.filter(item => item.id !== id);
        setSchedules(newSchedules);
        setDeleteSchedule('1 file deleted!');
        setShowSelectedHeader([]);

        timeoutDeleted = setTimeout(() => {
            setDeleteSchedule('');
            dataProvider('DELETE', 'schedule-template', {
                id
            }).then(res => {
                console.log('On Delete one template:', res);
                getTemplates();
            }).catch(error => {
                console.error('On Delete:', error);
            });
        }, 6000);
    }

    const onCopy = (id) => {
        const schedule = schedules.find(item => item.id === id);
        const data = {
            name: schedule.name + ' copy',
            scheduleMessages: schedule.scheduleMessages.map(item => {
                return {
                    subject: item.subject,
                    message: item.message,
                    day: item.day,
                    hour: item.hour,
                    minute: item.minute
                }
            })
        }
        dataProvider('CREATE', 'schedule-template', {
            data
        }).then(res => {
            console.log('Copy schedule success:', res);
            getTemplates();
        }).catch(error => {
            // TODO: Should show error message
            alert('Copy schedule error:', error);
        });
    }

    const onCloseCreateForm = (isSuccess = false) => {
        if (isSuccess) {
            getTemplates();
        }
        setShowCreateDialog(false);
        setCurrentEditSchedule(null);
    }

    const openCreateTemplateForm = (index) => {
        const selectedTemplate = schedules[index];
        if (selectedTemplate) {
            setCurrentEditSchedule(selectedTemplate);
            setShowCreateDialog(true);
        } else {
            // TODO: Out of range error
        }
    }

    return (
        <Fragment>
            <Box padding={3} bgcolor='#ffffff'>
                <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                    {
                        isCreate &&
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => setShowCreateDialog(true)}
                            className={classes.button}
                        >{translate(`resources.messages.createSchedule`)}
                            <Add width={13} style={{ marginLeft: 5, width: 13, height: 13 }} />
                        </Button>
                    }
                    <Box flex={1} display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
                        <Search placeholder={translate(`resources.messages.searchScheduler`)} onChangeText={setSearchText} getList={getTemplates} />
                    </Box>
                </Box>
                <Box pt={4} display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <Typography className={classes.textCountPagination}>{getTextPagination(schedules, pagination, totalItems, translate)}</Typography>
                        <img src={icChevronLeft} alt='' className={classes.btnIconOnly} style={{ marginLeft: 8 }} onClick={onPrevious} />
                        <img src={icChevronRight} alt='' className={classes.btnIconOnly} style={{ marginLeft: 4 }} onClick={onNext} />
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <img src={icRefresh} alt=''
                            className={classes.btnIconOnly}
                            onClick={onRefresh}
                            style={{ marginRight: 4, width: 16, height: 16, padding: 4 }}
                        />
                        <Typography className={classes.textCountPagination}>{translate(`resources.alarm.refresh`)}</Typography>
                    </Box>
                </Box>
            </Box>
            <Box mt={2.75}>
                <Snackbar
                    id='snackbar-delete-message'
                    show={deleteSchedule.length > 0}
                    message={deleteSchedule}
                    undo={onUndoDeleted}
                    onClose={() => setDeleteSchedule('')}
                />
                <TableSchedulerHeaders
                    showSelectedHeader={showSelectedHeader}
                    onChangeSortable={onChangeSortable}
                    order={order}
                    onCheckAll={onCheckAll}
                    onDelete={onDeleteMany}
                    checkall={checkall}
                    onCheck={onCheck}
                    isDelete={isDelete}
                />
                <Loading show={loading} />

                {
                    !loading &&
                    <Box style={{ maxHeight: "60vh", overflow: 'auto' }}>
                        {
                            schedules.map((item, index) =>
                                <TableSchedulerItem
                                    key={item.id + ''}
                                    item={item}
                                    onCheck={onCheck}
                                    onDelete={onDeleteOne}
                                    onCopy={onCopy}
                                    onEdit={() => openCreateTemplateForm(index)}
                                    isDelete={isDelete}
                                    isUpdate={isUpdate}
                                />
                            )
                        }
                    </Box>
                }

                <TablePagination
                    currentPage={page}
                    pages={pages}
                    onChangePage={onChangePage}
                    onPrevious={onPrevious}
                    onNext={onNext}
                />
            </Box>
            <DialogCreateEditSchedule
                open={showCreateDialog}
                onClose={onCloseCreateForm}
                schedule={currentEditSchedule}
            />
        </Fragment>
    );
}

TabScheduler.propTypes = {

};

export default TabScheduler;
